import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { OneLinkChunksEnum, useOneLinkSummary } from '../../contexts/OneLinkSummaryProvider';
import { OneLinkStatusEnum } from '../../types/oneLink';
import { offersMarketplaceContent } from '../../utils/marketplaceSend';
import { DeactivateOneLinkModal, Loader } from '../index';
import {
  AdditionalSection,
  ConfirmedSendsSection,
  CustomScenariosSection,
  InventorySection,
  SecuritySection,
  ShippingSection,
  SummarySection,
  TemplateSection,
  TwoFASection,
} from './components';
import { Sidebar } from './components/common';
import { SidebarSizesEnum } from './components/common/Sidebar';

interface IProps {
  reportMode: boolean;
}

const OneLinkSummaryComponent: React.FC<IProps> = ({ reportMode }) => {
  const gridSizes = { xs: 12, md: 6, lg: 4 };

  const {
    handleEdit,
    onSubmit,
    modes: editModes,
    onDeactivate,
    handleValidation,
    Form: EditForm,
    title: sidebarTitle,
    active: sidebarMode,
    instance,
    isLoading,
    errors: validationErrors,
  } = useOneLinkSummary();

  const [isOpenDeactivateModal, setIsOpenDeactivateModal] = useState(false);

  const handleOpenDeactivateModal = useCallback(() => {
    if (onDeactivate) {
      setIsOpenDeactivateModal(true);
    }
  }, [onDeactivate]);

  const handleCloseDeactivateModal = useCallback(() => {
    setIsOpenDeactivateModal(false);
  }, []);

  const isActive = useMemo(() => instance?.status === OneLinkStatusEnum.Active, [instance?.status]);

  const offersMarketplace = useMemo(() => offersMarketplaceContent(instance), [instance]);

  const hasCustomScenarios = useMemo(() => Boolean(instance?.custom_scenarios?.length), [instance]);

  if (!instance) {
    return null;
  }

  return (
    <Grid container sx={{ width: '100%' }} spacing={2}>
      <Grid size={12}>
        <SummarySection
          instance={instance}
          reportMode={reportMode}
          actions={[
            {
              title: 'Edit',
              onClick: () => handleEdit(editModes.SummaryInfo),
              disabled: false,
            },
            {
              title: !isActive ? 'Activate' : 'Deactivate',
              onClick: () => (!isActive ? handleValidation?.() : handleOpenDeactivateModal()),
            },
          ]}
          errors={validationErrors}
        />
      </Grid>
      <Grid size={gridSizes}>
        <InventorySection
          instance={instance}
          onEdit={offersMarketplace ? undefined : () => handleEdit(editModes.InventoryInfo)}
          errors={validationErrors}
        />
      </Grid>
      <Grid size={gridSizes}>
        <TemplateSection instance={instance} onSubmit={onSubmit} errors={validationErrors} />
      </Grid>
      <Grid size={gridSizes}>
        <ShippingSection
          instance={instance}
          onEdit={() => handleEdit(editModes.ShippingInfo)}
          errors={validationErrors}
        />
      </Grid>
      <Grid container size={gridSizes} spacing={2} sx={{ mb: 2, alignContent: 'flex-start' }}>
        <TwoFASection
          instance={instance}
          reportMode={reportMode}
          onSubmit={onSubmit}
          onEdit={() => handleEdit(editModes.TwoFactorInfo)}
        />
        <SecuritySection
          instance={instance}
          onEdit={() => handleEdit(editModes.SecurityInfo)}
          errors={validationErrors}
        />
        <AdditionalSection
          instance={instance}
          onEdit={() => handleEdit(editModes.AdditionalInfo)}
          errors={validationErrors}
        />
      </Grid>
      <Grid size={{ xs: 12, lg: 8 }} sx={{ mb: 2 }}>
        <ConfirmedSendsSection instance={instance} />
      </Grid>
      {hasCustomScenarios && (
        <Grid size={{ xs: 12, lg: 4 }} sx={{ mb: 2 }}>
          <CustomScenariosSection instance={instance} />
        </Grid>
      )}

      <Sidebar
        size={
          sidebarMode === OneLinkChunksEnum.InventoryInfo || sidebarMode === OneLinkChunksEnum.TwoFactorInfo
            ? SidebarSizesEnum.xl
            : undefined
        }
        open={sidebarMode !== null}
        onClose={() => handleEdit(null)}
        title={sidebarTitle}
      >
        {({ ref, onClose }) => (
          <Box sx={{ height: '100%' }}>
            <Loader isLoading={isLoading} />
            <EditForm onSubmit={onSubmit} instance={instance} ref={ref} onClose={onClose} />
          </Box>
        )}
      </Sidebar>
      <DeactivateOneLinkModal
        isOpenDeactivateDialog={isOpenDeactivateModal}
        handleCloseDeactivateDialog={handleCloseDeactivateModal}
        selectedItem={instance}
        handleDisableOneLink={() => onDeactivate?.() || Promise.resolve()}
      />
    </Grid>
  );
};

export default OneLinkSummaryComponent;
